import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { ESPokemonSingleProduct } from '@ka-nabellinc/tcg-platform-es-schema'
import type { Rank } from '@/types/product'
import type { PokemonCart, PokemonCartItem } from '@/types/cart'
import { LOCAL_STORAGE_KEYS } from '@/const/localstorage'
import { sortByRank } from '@/utils/product'
import type { NormalizedAvailabilityValidationResult } from '@/types/order'

export const useCartStore = defineStore('cart', () => {
  // State
  const pokemonCart = ref<PokemonCart>({ items: [] })
  const showCart = ref(false)
  const normalizedAvailabilityValidationResults = ref<NormalizedAvailabilityValidationResult[]>([])

  // Computed
  const pokemonCartTotalOrderNum = computed(() => pokemonCart.value.items.reduce((acc, ac) => acc + ac.orderNum, 0))
  const pokemonCartMerchandiseAmount = computed(() => pokemonCart.value.items.reduce((acc, ac) => acc + (ac.orderNum * ac.unitPrice), 0))
  const normalizedAvailabilityValidationResultsByInventoryId = computed(() => {
    return normalizedAvailabilityValidationResults.value.reduce((acc, ac) => {
      if (ac.productType === 'single') {
        for (const inventory of ac.inventories) {
          acc[inventory.inventoryId] = inventory.messages
        }
      }

      return acc
    }, {} as { [inventoryId: string]: string[] })
  })

  // Actions
  // カートの状態を復元
  const restoreCart = () => {
    if (!process.client) return
    const pokemonCartState = localStorage.getItem(LOCAL_STORAGE_KEYS.POKEMON_CART)
    if (pokemonCartState) {
      // FIXME: parseできるかtry..catchを仕込む
      pokemonCart.value = JSON.parse(pokemonCartState)
    }
  }

  // カートの変更を永続化
  watch(pokemonCart, () => {
    // カートを端末に保存
    if (process.client) {
      const serialized = JSON.stringify(pokemonCart.value)
      const shouldChange = serialized !== localStorage.getItem(LOCAL_STORAGE_KEYS.POKEMON_CART)
      if (shouldChange) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.POKEMON_CART, JSON.stringify(pokemonCart.value))
      }
    }
  }, { deep: true })

  const addSingleProductToPokemonCart = (params: { product: ESPokemonSingleProduct } & { [rank in Rank]: number }) => {
    const { product, s, a, b, c } = params

    // カートに追加
    function addItem(rank: Rank, orderNum: number) {
      const sameItemIndex = pokemonCart.value.items.findIndex(item => item.productType === 'single' && item.product.inventory_product_id === product.inventory_product_id && item.rank === rank)
      if (sameItemIndex >= 0) {
        pokemonCart.value.items[sameItemIndex].orderNum += orderNum
      } else {
        const targetInventory = product[`rank_${rank}_inventory`]
        pokemonCart.value.items.push({
          productType: 'single',
          product,
          rank,
          inventoryId: targetInventory.inventory_id,
          orderNum,
          unitPrice: targetInventory.selling_price
        })
      }
    }
    if (s) addItem('s', s)
    if (a) addItem('a', a)
    if (b) addItem('b', b)
    if (c) addItem('c', c)

    // カート追加時は状態違いの商品全体を先頭に移動する
    const sameProductItems = pokemonCart.value.items.filter(item => item.productType === 'single' && item.product.inventory_product_id === product.inventory_product_id)
    const otherProductItems = pokemonCart.value.items.filter(item => !(item.productType === 'single' && item.product.inventory_product_id === product.inventory_product_id))

    pokemonCart.value.items = [
      ...sortByRank(sameProductItems),
      ...otherProductItems
    ]
  }
  const incementeSingleProductFromPokemonCart = ({ rank, product: { inventory_product_id } }: PokemonCartItem) => {
    const targetItemIndex = pokemonCart.value.items.findIndex(item => item.productType === 'single' && item.product.inventory_product_id === inventory_product_id && item.rank === rank)
    if (targetItemIndex < 0) {
      console.error('target item not found')
      return
    }
    // 枚数制限等のvalidationはComponent側で実施
    pokemonCart.value.items[targetItemIndex].orderNum++
  }
  const decrementSingleProductFromPokemonCart = ({ rank, product: { inventory_product_id } }: PokemonCartItem) => {
    const targetItemIndex = pokemonCart.value.items.findIndex(item => item.productType === 'single' && item.product.inventory_product_id === inventory_product_id && item.rank === rank)
    const targetItem = pokemonCart.value.items[targetItemIndex]
    if (!targetItem) {
      console.error('target item not found')
      return
    }
    if (targetItem.orderNum <= 1) {
      // カートから商品を削除
      pokemonCart.value.items.splice(targetItemIndex, 1)
    } else {
      pokemonCart.value.items[targetItemIndex].orderNum--
    }
  }
  const removeSingleProductFromPokemonCart = (inventoryId: string) => {
    pokemonCart.value.items = pokemonCart.value.items.filter(item => item.inventoryId !== inventoryId)
  }
  const modifySingleProductOrderNumForPokemonCart = (inventoryId: string, orderNum: number) => {
    if (orderNum === 0) {
      removeSingleProductFromPokemonCart(inventoryId)
    } else {
      const targetItemIndex = pokemonCart.value.items.findIndex(item => item.inventoryId === inventoryId)
      pokemonCart.value.items[targetItemIndex].orderNum = orderNum
    }
  }
  const modifySingleProductUnitPriceForPokemonCart = (inventoryId: string, unitPrice: number) => {
    const targetItemIndex = pokemonCart.value.items.findIndex(item => item.inventoryId === inventoryId)
    pokemonCart.value.items[targetItemIndex].unitPrice = unitPrice
  }
  const modifySingleProductSeellingNumLimitForPokemonCart = (inventoryProductId: string, sellingNumlimit: number) => {
    // カート内の同一商品の和が購入制限を上回る時は、状態の低い商品を優先的に減少
    // Reference: https://docs.google.com/document/d/1qE1nVHDfcJxZ1u3aIpQcstLIuHpmPSprFzcAHrphLII/edit#heading=h.e2qvyp28wurw
    const sameProductItems = pokemonCart.value.items.filter(item => item.product.inventory_product_id === inventoryProductId)
    const currentTotalNum = sameProductItems.reduce((acc, ac) => acc + ac.orderNum, 0)
    let numToReduce = currentTotalNum - sellingNumlimit
    if (!numToReduce) return

    // 状態の低い順にソート
    const ascSortedItems = sortByRank(sameProductItems).reverse()
    for (const item of ascSortedItems) {
      if (item.orderNum < numToReduce) {
        removeSingleProductFromPokemonCart(item.inventoryId)
        numToReduce -= item.orderNum
      } else {
        modifySingleProductOrderNumForPokemonCart(item.inventoryId, item.orderNum - numToReduce)
        break
      }
    }
  }
  const clearPokemonCart = () => {
    pokemonCart.value = { items: [] }
  }

  // カートを閉じるタイミングでvalidation resultを削除
  watch(showCart, val => {
    if (!val && !!normalizedAvailabilityValidationResults.value.length) {
      normalizedAvailabilityValidationResults.value = []
    }
  }, { immediate: false})

  return {
    showCart,
    normalizedAvailabilityValidationResults,
    normalizedAvailabilityValidationResultsByInventoryId,
    addSingleProductToPokemonCart,
    incementeSingleProductFromPokemonCart,
    decrementSingleProductFromPokemonCart,
    removeSingleProductFromPokemonCart,
    modifySingleProductOrderNumForPokemonCart,
    modifySingleProductUnitPriceForPokemonCart,
    modifySingleProductSeellingNumLimitForPokemonCart,

    restoreCart,

    pokemonCart,
    pokemonCartTotalOrderNum,
    pokemonCartMerchandiseAmount,
    clearPokemonCart,
  }
})

export type CartStore = ReturnType<typeof useCartStore>
